import { scale, smoothPath, segments } from './svgPath';
import { dayMonth } from '../../pages/class_performance/lib/cpBits';

export interface WeeklyPoint { week_start: string; avg: number | null }

interface Props {
  section: WeeklyPoint[];
  grade: WeeklyPoint[];
  ariaLabel: string;
}

const lineOf = (values: (number | null)[], x: (i: number) => number, y: (v: number) => number) =>
  segments(values).filter((r) => r.length > 1).map((r) => smoothPath(r.map((p) => [x(p.i), y(p.v)] as [number, number])));

/** Design SD "10B vs Grade 10": the section's weekly line (accent, ends in a ringed dot) against a dashed grade line, y auto-scaled. */
export default function SectionVsGradeChart({ section, grade, ariaLabel }: Props) {
  const weeks = [...new Set([...section, ...grade].map((p) => p.week_start))].sort();
  const valuesFor = (pts: WeeklyPoint[]) => weeks.map((w) => pts.find((p) => p.week_start === w)?.avg ?? null);
  const sv = valuesFor(section);
  const gv = valuesFor(grade);
  const all = [...sv, ...gv].filter((v): v is number => v != null);
  if (all.length === 0) return null;
  const lo = Math.floor(Math.min(...all) - 1);
  const hi = Math.max(lo + 2, Math.ceil(Math.max(...all) + 1));
  const x = scale(0, Math.max(1, weeks.length - 1), 40, 380);
  const y = scale(lo, hi, 140, 20);
  const grid = [lo, Math.round((lo + hi) / 2), hi];
  const nTicks = Math.min(weeks.length, 4);
  const tickIdx = nTicks <= 1 ? [0] : Array.from({ length: nTicks }, (_, k) => Math.round((k * (weeks.length - 1)) / (nTicks - 1)));
  const sRuns = segments(sv).flat();
  const last = sRuns[sRuns.length - 1];
  return (
    <svg viewBox="0 0 400 170" className="block h-auto w-full" role="img" aria-label={ariaLabel}>
      {grid.map((t) => (
        <g key={t}>
          <line x1={30} x2={390} y1={y(t)} y2={y(t)} className="stroke-line" />
          <text x={22} y={y(t) + 4} textAnchor="end" className="fill-muted font-mono text-[11px]">{t}</text>
        </g>
      ))}
      {lineOf(gv, x, y).map((d, k) => (
        <path key={`g${k}`} d={d} fill="none" className="stroke-on-navy-muted" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" strokeDasharray="6 5" />
      ))}
      {lineOf(sv, x, y).map((d, k) => (
        <path key={`s${k}`} d={d} fill="none" className="stroke-accent" strokeWidth={3} strokeLinejoin="round" strokeLinecap="round" />
      ))}
      {last && <circle cx={x(last.i)} cy={y(last.v)} r={5} className="fill-surface stroke-accent" strokeWidth={3} />}
      {tickIdx.map((i) => (
        <text key={i} x={x(i)} y={162} textAnchor="middle" className="fill-muted font-mono text-[11px]">{dayMonth(weeks[i])}</text>
      ))}
    </svg>
  );
}
